import type React from "react";

import type { IProduct } from "../../models/catalogue";

/**
 * The contract between the grid and the tile it renders per product.
 *
 * Separate from `models.ts`, which describes what the CMS sends -- io-ts
 * codecs over the StreamField payload and the plain-data config types they
 * feed -- and which says of itself that it stays presentation-free. What is
 * here is the opposite: a CSS class, a DOM ref, and a function returning a
 * `ReactNode`. Keeping the two apart is also what lets a site import its
 * rendering contract from a module that says so.
 */

/**
 * The class that makes an element a grid item.
 *
 * `GridContent` passes it to `MasonryGrid` as `itemSelector`, `ProductCard`
 * puts it on the default tile, and every custom tile receives it through
 * `IGridTileProps` -- so the selector and what it selects move together.
 *
 * Not the sole definition of the name, though: several sites' global
 * stylesheets select `.product-grid-card` by hand, so renaming the value here
 * still means grepping the SCSS. What this does guarantee is that the JS side
 * cannot drift against itself, which is the failure with no error attached --
 * a selector that matches nothing leaves `MasonryGrid` with no tiles to lay
 * out, position, or equalise.
 */
export const GRID_TILE_CLASS = "product-grid-card";

/**
 * What the grid hands a tile so it can be laid out.
 *
 * Spread onto the tile's outermost element. Both fields are Masonry's, not
 * the tile's: it measures and repositions that element by the class, and
 * reaches for it by the ref when equalising heights and focusing newly added
 * tiles. Handing them down rather than asking for them back is what keeps a
 * tile from silently dropping out of the layout.
 *
 * Kept identity-stable per tile by `GridContent`, so a `React.memo`'d tile can
 * actually get a cache hit. Nothing that closes over per-render state belongs
 * in here for that reason -- see `BuildGridTile`'s `renderDefault`.
 *
 * `Readonly`, and shallow on purpose: the bag is cached and shared, so a tile
 * assigning to `className` would corrupt it for every later render of that
 * tile -- and since the class is what `MasonryGrid` selects by, the failure
 * would be the silent unlaid-out grid described above. Shallow because
 * `ref.current` must stay writable: that is how a tile hands its element back,
 * and the project's deep `Immutable<>` would forbid it.
 *
 * One obligation is not in here, because it cannot be: **a tile must give
 * itself a width.** `GridContent` passes `MasonryGrid` no `columnWidth`, so
 * masonry measures the first tile element and fits as many across as that
 * allows -- a tile with no width of its own therefore collapses the grid to a
 * single full-bleed column. It is the same silent failure handing down the
 * class and the ref exists to prevent, and the only one left implicit; the
 * shared `ProductCard` answers it with 33% / 50% in its own module. Making it
 * structural means the grid stating its own `columnWidth`, which changes
 * masonry's positioning for every site at once.
 */
export type IGridTileProps = Readonly<{
    ref: React.RefObject<HTMLDivElement | null>;
    className: string;
}>;

/**
 * Builds the tile a grid renders for one product.
 *
 * A site passes this to render its own tile instead of the shared
 * `ProductCard` -- so the grid does not grow a branch per site, and
 * `ProductCard` does not grow options for presentation one brand wants.
 *
 * `key` is not the tile's concern: `MasonryGrid` already renders each result
 * inside a keyed fragment.
 *
 * When set, it decides *per product* whether `ProductCard` renders at all.
 * `cardType`, `disableHoverOverlay` and `buildExternalCallout` reach nothing
 * for a product the tile draws itself -- but they still configure the card
 * `renderDefault` produces, so they stay live for every product the tile
 * hands back. Which is why this cannot become a discriminated union against
 * those props: the fallback needs them.
 *
 * `renderDefault` renders the shared `ProductCard` exactly as this grid would
 * have, for tiles that opt back out per product. Without it a caller has to
 * reconstruct that call itself, which means copying defaults the grid owns
 * (`gridVariant`, `cardType`) and quietly dropping the ones it forgets.
 *
 * Return its result directly. It is already a whole tile -- `ProductCard`
 * puts `GRID_TILE_CLASS` on itself and takes the same ref -- so wrapping it
 * in an element that also spreads `tileProps` gives `MasonryGrid` two
 * elements matching `itemSelector`, one nested in the other, and two writers
 * of `ref.current`.
 *
 * It is a third argument rather than a field on `tileProps` because it closes
 * over the product and the grid's current props: putting it in the bag would
 * either make the bag a new object every render -- defeating the memoisation
 * the bag's stable identity exists for -- or leave a cached bag holding a
 * stale closure.
 */
export type BuildGridTile = (
    product: IProduct,
    tileProps: IGridTileProps,
    renderDefault: () => React.ReactNode,
) => React.ReactNode;
